import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTimeTracking } from '../contexts/TimeTrackingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import UserHoverCard from '../components/UserHoverCard';
import TaskListAsana from '../components/tasks/TaskListAsana';
import TeamMemberFilter from '../components/tasks/TeamMemberFilter';
import TaskReminderPicker from '../components/tasks/TaskReminderPicker';
import {
    Plus, Clock,
    Calendar as CalendarIcon, Paperclip, Upload,
    Play, Square, Trash2, User,
    RefreshCw, FileText, Image, File, X, Timer, Edit3, Archive,
    MoreHorizontal, UserCog, RotateCcw, Bell
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Default statuses - will be replaced by API data
const defaultStatusConfig = {
    open: { label: 'Offen', emoji: '🟢', color: '#10b981' },
    in_progress: { label: 'In Arbeit', emoji: '🟡', color: '#eab308' },
    review: { label: 'Review', emoji: '🔵', color: '#3b82f6' },
    completed: { label: 'Erledigt', emoji: '🎉', color: '#10b981', is_done: true }
};

// Helper to convert hex color to Tailwind-like classes
const getStatusColorClasses = (hexColor) => {
    // Map common colors to Tailwind classes for better styling
    const colorMap = {
        '#10b981': 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
        '#22c55e': 'bg-green-500/10 text-green-600 border-green-500/30',
        '#eab308': 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
        '#f59e0b': 'bg-amber-500/10 text-amber-600 border-amber-500/30',
        '#3b82f6': 'bg-blue-500/10 text-blue-600 border-blue-500/30',
        '#6366f1': 'bg-indigo-500/10 text-indigo-600 border-indigo-500/30',
        '#8b5cf6': 'bg-violet-500/10 text-violet-600 border-violet-500/30',
        '#ec4899': 'bg-pink-500/10 text-pink-600 border-pink-500/30',
        '#ef4444': 'bg-red-500/10 text-red-600 border-red-500/30',
        '#f97316': 'bg-orange-500/10 text-orange-600 border-orange-500/30',
        '#14b8a6': 'bg-teal-500/10 text-teal-600 border-teal-500/30',
        '#06b6d4': 'bg-cyan-500/10 text-cyan-600 border-cyan-500/30',
        '#6b7280': 'bg-gray-500/10 text-gray-600 border-gray-500/30',
    };
    
    // Find closest color match or use inline style fallback
    const lowerHex = hexColor?.toLowerCase();
    if (colorMap[lowerHex]) {
        return colorMap[lowerHex];
    }
    
    // Default fallback with inline style support
    return `bg-[${hexColor}]/10 text-[${hexColor}] border-[${hexColor}]/30`;
};

const priorityConfig = {
    0: { label: 'Nicht dringend', color: 'border-l-green-500' },
    1: { label: 'Hoch', color: 'border-l-red-500' },
    2: { label: 'Mittel', color: 'border-l-yellow-500' },
    3: { label: 'Niedrig', color: 'border-l-gray-400' }
};

const recurrenceOptions = [
    { value: 'none', label: 'Keine Wiederholung' },
    { value: 'daily', label: 'Täglich' },
    { value: 'every_2_days', label: 'Alle 2 Tage' },
    { value: 'every_3_days', label: 'Alle 3 Tage' },
    { value: 'every_4_days', label: 'Alle 4 Tage' },
    { value: 'every_5_days', label: 'Alle 5 Tage' },
    { value: 'weekly', label: 'Wöchentlich' },
    { value: 'biweekly', label: 'Alle 2 Wochen' },
    { value: 'monthly', label: 'Monatlich' }
];

// Compact Live Timer Component for Task Detail
const LiveTaskTimer = ({ task, isActive, elapsedSeconds, onStart, onStop }) => {
    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const totalSeconds = task?.total_time_seconds || 0;
    const displayTime = isActive ? elapsedSeconds : 0;

    return (
        <div className="flex items-center gap-4 p-3 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20">
            {/* Timer display */}
            <div className="flex-1">
                <div className="flex items-center gap-2">
                    {isActive && (
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                        </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                        {isActive ? 'Zeit läuft' : 'Zeiterfassung'}
                    </span>
                </div>
                <p className={`text-2xl font-mono font-bold tracking-tight ${isActive ? 'text-primary' : ''}`}>
                    {isActive ? formatTime(displayTime) : formatTime(totalSeconds)}
                </p>
            </div>
            
            {/* Stats */}
            <div className="text-right text-xs">
                <p className="text-muted-foreground">Gesamt</p>
                <p className="font-semibold">{formatTime(totalSeconds + (isActive ? displayTime : 0))}</p>
            </div>
            
            {/* Play/Stop button */}
            <Button
                size="icon"
                className={`rounded-full w-10 h-10 ${isActive ? 'bg-destructive hover:bg-destructive/90' : 'bg-primary hover:bg-primary/90'}`}
                onClick={isActive ? onStop : onStart}
                data-testid="task-timer-button"
            >
                {isActive ? (
                    <Square className="w-4 h-4" fill="white" />
                ) : (
                    <Play className="w-4 h-4 ml-0.5" fill="white" />
                )}
            </Button>
        </div>
    );
};

// File icon helper
const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return Image;
    if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) return FileText;
    return File;
};

const TasksPage = () => {
    const { api, user } = useAuth();
    const isAdmin = user?.role === 'chef';
    const { isTracking, activeTaskId, activeProjectId, activeCustomerId, startTracking, stopTracking, cancelTracking, elapsedSeconds, formattedTime } = useTimeTracking();
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedAsanaTask, setSelectedAsanaTask] = useState(null);
    // Admin: Mitarbeiter-Filter (IDs), deren Tasks statt der eigenen angezeigt werden
    const [teamFilter, setTeamFilter] = useState([]);
    const [teamFilterLoaded, setTeamFilterLoaded] = useState(false);
    const [teamTasks, setTeamTasks] = useState([]);
    const [teamTasksLoading, setTeamTasksLoading] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [timePerUser, setTimePerUser] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [taskSubmitting, setTaskSubmitting] = useState(false);
    const [showStopModal, setShowStopModal] = useState(false);
    const [showSwitchModal, setShowSwitchModal] = useState(false);
    const [pendingTaskSwitch, setPendingTaskSwitch] = useState(null);
    const [selectedCustomerForTime, setSelectedCustomerForTime] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [uploading, setUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [activityTypes, setActivityTypes] = useState([]);
    const [taskStatuses, setTaskStatuses] = useState([]);
    const [stopTimeForm, setStopTimeForm] = useState({
        customer_id: '',
        task_id: '',
        activity_type: '',
        description: '',
        is_pause: false
    });
    const fileInputRef = useRef(null);
    const dropZoneRef = useRef(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        status: 'open',
        priority: 2,
        deadline: '',
        deadline_time: '',
        assigned_to: [],
        account_manager_id: '',
        project_id: '',
        is_recurring: false,
        recurrence_pattern: 'none',
        category: '',
        estimated_hours: '',
        reminders: []
    });

    const fetchData = useCallback(async () => {
        try {
            const [tasksRes, usersRes, projectsRes, customersRes, settingsRes, statusesRes] = await Promise.all([
                api.get('/tasks'),
                api.get('/users'),
                api.get('/projects'),
                api.get('/customers'),
                api.get('/system/settings'),
                api.get('/task-statuses')
            ]);
            setTasks(tasksRes.data);
            setUsers(usersRes.data);
            setProjects(projectsRes.data);
            setCustomers(customersRes.data.filter(c => !c.is_archived));
            setSettings(settingsRes.data);
            setActivityTypes(settingsRes.data?.activity_types || []);
            setTaskStatuses(statusesRes.data || []);
        } catch (error) {
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    }, [api]);

    // Build dynamic statusConfig from API data
    const statusConfig = useMemo(() => {
        if (taskStatuses.length === 0) {
            // Fallback to defaults if no statuses loaded
            return Object.entries(defaultStatusConfig).reduce((acc, [id, config]) => {
                acc[id] = {
                    ...config,
                    color: getStatusColorClasses(config.color),
                    rawColor: config.color || '#6b7280'
                };
                return acc;
            }, {});
        }
        
        return taskStatuses.reduce((acc, status) => {
            acc[status.id] = {
                label: status.name,
                emoji: status.emoji || '⚪',
                color: getStatusColorClasses(status.color),
                rawColor: status.color || '#6b7280',
                is_done: status.is_done || false
            };
            return acc;
        }, {});
    }, [taskStatuses]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Admin: gespeicherte Mitarbeiter-Auswahl laden (dauerhaft, pro User)
    useEffect(() => {
        if (!isAdmin) return;
        const loadTeamFilter = async () => {
            try {
                const res = await api.get('/users/me/preferences');
                const saved = res.data?.task_team_filter;
                if (Array.isArray(saved)) setTeamFilter(saved);
            } catch (e) {
            } finally {
                setTeamFilterLoaded(true);
            }
        };
        loadTeamFilter();
    }, [api, isAdmin]);

    // Admin: Auswahl aendern + speichern
    const handleTeamFilterChange = async (ids) => {
        setTeamFilter(ids);
        setSelectedAsanaTask(null);
        try {
            await api.post('/users/me/preferences', { task_team_filter: ids });
        } catch (e) {
            toast.error('Mitarbeiter-Auswahl konnte nicht gespeichert werden');
        }
    };

    // Admin: Tasks der ausgewaehlten Mitarbeiter laden (Team-Hub-Endpunkt, view_all)
    const fetchTeamTasks = useCallback(async () => {
        if (!isAdmin || teamFilter.length === 0) {
            setTeamTasks([]);
            return;
        }
        setTeamTasksLoading(true);
        try {
            const results = await Promise.all(
                teamFilter.map(id => api.get('/tasks', { params: { assigned_to: id, view_all: true } }))
            );
            const merged = new Map();
            results.forEach(r => (r.data || []).forEach(t => merged.set(t.id, t)));
            setTeamTasks(Array.from(merged.values()));
        } catch (error) {
            toast.error('Mitarbeiter-Tasks konnten nicht geladen werden');
        } finally {
            setTeamTasksLoading(false);
        }
    }, [api, isAdmin, teamFilter]);

    // Neu laden bei Auswahl-Aenderung und nach jeder Aenderung an Tasks (fetchData setzt `tasks`)
    useEffect(() => {
        if (!teamFilterLoaded) return;
        fetchTeamTasks();
    }, [fetchTeamTasks, teamFilterLoaded, tasks]);

    // Listen for AI Assistant events to open modal with prefilled data
    useEffect(() => {
        const handleOpenTaskModal = (event) => {
            const prefill = event.detail?.prefill || {};
            setFormData(prev => ({
                ...prev,
                title: prefill.title || '',
                description: prefill.description || '',
                assigned_to: prefill.assigned_to ? [prefill.assigned_to] : [],
                deadline: prefill.deadline || '',
                priority: prefill.priority !== undefined ? prefill.priority : 2
            }));
            setEditingTask(null);
            setShowCreateModal(true);
        };

        window.addEventListener('openTaskModal', handleOpenTaskModal);
        return () => window.removeEventListener('openTaskModal', handleOpenTaskModal);
    }, []);
    // Refresh selected task when it changes
    useEffect(() => {
        if (selectedTask) {
            const updated = [...tasks, ...teamTasks].find(t => t.id === selectedTask.id);
            if (updated) {
                setSelectedTask(updated);
            }
        }
    }, [tasks, teamTasks]);

    // Fetch time per user when task is selected
    useEffect(() => {
        const fetchTimePerUser = async () => {
            if (selectedTask?.id) {
                try {
                    const res = await api.get(`/tasks/${selectedTask.id}/time-per-user`);
                    setTimePerUser(res.data);
                } catch (error) {
                    setTimePerUser([]);
                }
            } else {
                setTimePerUser([]);
            }
        };
        fetchTimePerUser();
    }, [selectedTask?.id]);

    const handleCreateTask = async () => {
        if (taskSubmitting) return;
        try {
            setTaskSubmitting(true);
            const payload = { ...formData };
            
            // Combine date and time for deadline
            if (payload.deadline && payload.deadline_time) {
                payload.deadline = `${payload.deadline}T${payload.deadline_time}`;
            }
            delete payload.deadline_time;
            
            if (!payload.deadline) delete payload.deadline;
            if (!payload.project_id || payload.project_id === 'none') delete payload.project_id;
            if (!payload.account_manager_id || payload.account_manager_id === 'none') delete payload.account_manager_id;
            if (!payload.category || payload.category === 'none') delete payload.category;
            if (payload.estimated_hours) {
                payload.estimated_hours = parseFloat(payload.estimated_hours);
            } else {
                delete payload.estimated_hours;
            }
            if (!payload.is_recurring) {
                delete payload.is_recurring;
                delete payload.recurrence_pattern;
            }
            
            await api.post('/tasks', payload);
            toast.success('Task erstellt');
            setShowCreateModal(false);
            setFormData({ 
                title: '', description: '', status: 'open', priority: 2, 
                deadline: '', deadline_time: '', assigned_to: [], account_manager_id: '',
                project_id: '', is_recurring: false, recurrence_pattern: 'none',
                category: '', estimated_hours: '', reminders: []
            });
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Erstellen');
        } finally {
            setTaskSubmitting(false);
        }
    };

    const handleEditSave = async () => {
        if (!editingTask || taskSubmitting) return;
        try {
            setTaskSubmitting(true);
            // Clean empty strings that would cause backend validation errors
            const payload = { ...formData };
            if (payload.estimated_hours === '' || payload.estimated_hours === null || payload.estimated_hours === undefined) {
                delete payload.estimated_hours;
            } else if (typeof payload.estimated_hours === 'string') {
                const parsed = parseFloat(payload.estimated_hours.replace(',', '.'));
                if (Number.isFinite(parsed)) {
                    payload.estimated_hours = parsed;
                } else {
                    delete payload.estimated_hours;
                }
            }
            ['project_id', 'customer_id', 'account_manager_id', 'category', 'deadline'].forEach((k) => {
                if (payload[k] === '') delete payload[k];
            });
            if (payload.recurrence_pattern === 'none' || payload.recurrence_pattern === '') {
                delete payload.recurrence_pattern;
            }
            await api.put(`/tasks/${editingTask.id}`, payload);
            toast.success('Task aktualisiert');
            setShowCreateModal(false);
            setEditingTask(null);
            fetchData();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? `Fehler: ${detail}` : 'Fehler beim Aktualisieren');
        } finally {
            setTaskSubmitting(false);
        }
    };

    const handleUpdateTask = async (taskId, updates) => {
        try {
            // Clean empty strings that would cause validation errors
            const cleanedUpdates = { ...updates };
            if (cleanedUpdates.estimated_hours === '' || cleanedUpdates.estimated_hours === null) {
                delete cleanedUpdates.estimated_hours;
            }
            if (cleanedUpdates.project_id === '') {
                delete cleanedUpdates.project_id;
            }
            if (cleanedUpdates.customer_id === '') {
                delete cleanedUpdates.customer_id;
            }
            if (cleanedUpdates.account_manager_id === '') {
                delete cleanedUpdates.account_manager_id;
            }
            if (cleanedUpdates.recurrence_pattern === 'none') {
                delete cleanedUpdates.recurrence_pattern;
            }

            await api.put(`/tasks/${taskId}`, cleanedUpdates);
            
            // Check if status is a "done" status (dynamically)
            const isDoneStatus = cleanedUpdates.status && statusConfig[cleanedUpdates.status]?.is_done;
            if (isDoneStatus) {
                // Stop tracking if this task was being tracked
                if (activeTaskId === taskId && isTracking) {
                    await stopTracking(null, null, '');
                }
                
                confetti({
                    particleCount: 100,
                    spread: 70,
                    origin: { y: 0.6 },
                    colors: ['#34c759', '#009dde', '#ffcc00']
                });
                toast.success('🎉 Task erledigt!');
            }
            
            // Optimistic update: apply changes locally first for instant UI feedback
            setTasks(prev => prev.map(t => {
                if (t.id !== taskId) return t;
                const updated = { ...t, ...cleanedUpdates, updated_at: new Date().toISOString() };
                return updated;
            }));

            // Then refresh from server in background
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Aktualisieren');
            throw error; // Re-throw so callers know it failed
        }
    };

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm('Task wirklich endgültig löschen?')) return;
        try {
            await api.delete(`/tasks/${taskId}`);
            toast.success('Task gelöscht');
            setSelectedTask(null);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const handleArchiveTask = async (taskId) => {
        try {
            await api.put(`/tasks/${taskId}/archive`);
            toast.success('Task archiviert');
            setSelectedTask(null);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Archivieren');
        }
    };

    const handleUnarchiveTask = async (taskId) => {
        try {
            await api.put(`/tasks/${taskId}/unarchive`);
            toast.success('Task wiederhergestellt');
            const res = await api.get(`/tasks/${taskId}`);
            setSelectedTask(res.data);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Wiederherstellen');
        }
    };

    const handleAddComment = async (taskId, content) => {
        const id = taskId || selectedTask?.id;
        const text = content || newComment;
        if (!text?.trim()) return;
        try {
            await api.post(`/tasks/${id}/comments`, { content: text });
            if (!taskId) setNewComment('');
            const res = await api.get(`/tasks/${id}`);
            if (!taskId) setSelectedTask(res.data);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Kommentieren');
        }
    };

    const handleDeleteComment = async (taskId, commentId) => {
        try {
            await api.delete(`/tasks/${taskId}/comments/${commentId}`);
            const res = await api.get(`/tasks/${taskId}`);
            if (selectedTask?.id === taskId) setSelectedTask(res.data);
            fetchData();
            toast.success('Kommentar gelöscht');
        } catch (error) {
            toast.error('Fehler beim Löschen des Kommentars');
        }
    };

    const handleFileUploadForTask = async (taskId, file) => {
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Hey Schwerlastversender! Nutz bitte unser Google Drive!');
            return;
        }
        setUploading(true);
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        try {
            await api.post(`/upload?task_id=${taskId}`, uploadFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Datei hochgeladen');
            const res = await api.get(`/tasks/${taskId}`);
            if (selectedTask?.id === taskId) setSelectedTask(res.data);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
        } finally {
            setUploading(false);
        }
    };

    const handleFileDelete = async (taskId, fileKey) => {
        try {
            await api.delete(`/tasks/${taskId}/files/${fileKey}`);
            const res = await api.get(`/tasks/${taskId}`);
            if (selectedTask?.id === taskId) setSelectedTask(res.data);
            fetchData();
            toast.success('Datei gelöscht');
        } catch (error) {
            toast.error('Fehler beim Löschen der Datei');
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selectedTask) return;
        await handleFileUploadForTask(selectedTask.id, file);
    };

    const uploadFile = async (file) => {
        // Check file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Hey Schwerlastversender! Nutz bitte unser Google Drive!');
            return;
        }

        setUploading(true);
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        try {
            await api.post(`/upload?task_id=${selectedTask.id}`, uploadFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Datei hochgeladen');
            const res = await api.get(`/tasks/${selectedTask.id}`);
            setSelectedTask(res.data);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
        } finally {
            setUploading(false);
            setIsDragOver(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Drag & Drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
            await uploadFile(files[0]);
        }
    };

    const handleTimeToggle = (task) => {
        if (isTracking && activeTaskId === task.id) {
            // Stop current task
            setShowStopModal(true);
        } else if (isTracking && activeTaskId !== task.id) {
            // Switch task - show confirmation
            setPendingTaskSwitch(task);
            setShowSwitchModal(true);
        } else {
            // Start new task - pre-fill customer from task if present
            const taskCustomer = task.customer_id ? customers.find(c => c.id === task.customer_id) : null;
            startTracking(task.id, task.project_id, task.customer_id || null, taskCustomer?.name || null);
            toast.success('⏱ Zeit gestartet');
        }
    };

    const handleSwitchTask = async () => {
        try {
            await stopTracking(null, null, '');
            const taskCustomer = pendingTaskSwitch.customer_id ? customers.find(c => c.id === pendingTaskSwitch.customer_id) : null;
            startTracking(pendingTaskSwitch.id, pendingTaskSwitch.project_id, pendingTaskSwitch.customer_id || null, taskCustomer?.name || null);
            toast.success('⏱ Zeit gewechselt');
            setShowSwitchModal(false);
            setPendingTaskSwitch(null);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Wechseln');
        }
    };

    const handleStopTime = async (customerId, customerName, description = '') => {
        try {
            await stopTracking(customerId, customerName, description);
            toast.success('Zeit gespeichert');
            setShowStopModal(false);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
    };

    const teamMode = isAdmin && teamFilter.length > 0;
    const sourceTasks = teamMode ? teamTasks : tasks;
    const activeTasks = sourceTasks.filter(t => !t.is_archived);
    const archivedTasks = sourceTasks.filter(t => t.is_archived);
    
    const filteredTasks = showArchived ? archivedTasks : activeTasks;

    // Auswaehlbare Status (ohne virtuellen "overdue"-Status) - fuer Task-Formulare
    const kanbanStatuses = useMemo(() => {
        return Object.entries(statusConfig).filter(([key]) => key !== 'overdue');
    }, [statusConfig]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8" data-testid="tasks-page">
            {/* Header */}
            <div className="flex flex-col gap-4 mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {showArchived ? '📦 Archiv' : '📋 Tasks'}
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            {teamMode ? 'Mitarbeiter-Ansicht: ' : ''}{activeTasks.length} aktiv{archivedTasks.length > 0 ? `, ${archivedTasks.length} archiviert` : ''}
                        </p>
                    </div>
                    <Button onClick={() => setShowCreateModal(true)} data-testid="create-task-button" className="flex-shrink-0">
                        <Plus className="w-4 h-4 mr-2" />
                        Neuer Task
                    </Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Link to="/tasks/recurring">
                        <Button variant="outline" size="sm" data-testid="recurring-tasks-link">
                            <RefreshCw className="w-4 h-4 mr-1.5" />
                            <span className="hidden sm:inline">Wiederkehrend</span>
                        </Button>
                    </Link>
                    {archivedTasks.length > 0 && (
                        <Button
                            variant={showArchived ? "default" : "outline"}
                            size="sm"
                            onClick={() => setShowArchived(!showArchived)}
                            data-testid="toggle-archived-tasks"
                        >
                            <Archive className="w-4 h-4 mr-1.5" />
                            {showArchived ? 'Aktive' : `Archiv (${archivedTasks.length})`}
                        </Button>
                    )}
                    {isAdmin && (
                        <TeamMemberFilter
                            users={users}
                            selectedIds={teamFilter}
                            onChange={handleTeamFilterChange}
                            loading={teamTasksLoading}
                        />
                    )}
                </div>
            </div>

            {/* Asana-style Task View */}
            <TaskListAsana
                tasks={filteredTasks}
                users={users}
                projects={projects}
                customers={customers}
                statusConfig={statusConfig}
                showCompleted={showArchived}
                highlightUserIds={teamMode ? teamFilter : []}
                onTaskSelect={(task) => {
                    if (task) {
                        setSelectedAsanaTask(task.id);
                    } else {
                        setSelectedAsanaTask(null);
                    }
                }}
                onOpenFullModal={(task) => {
                    setSelectedAsanaTask(null);
                    setSelectedTask(task);
                }}
                onTaskUpdate={handleUpdateTask}
                onTaskDelete={handleDeleteTask}
                onAddComment={handleAddComment}
                onDeleteComment={handleDeleteComment}
                onFileUpload={handleFileUploadForTask}
                onFileDelete={handleFileDelete}
                onTimeToggle={handleTimeToggle}
                isTracking={isTracking}
                activeTaskId={activeTaskId}
                selectedTaskId={selectedAsanaTask}
                elapsedSeconds={elapsedSeconds}
                formattedTime={formattedTime}
                onStartTracking={startTracking}
                onStopTracking={stopTracking}
                onCancelTracking={cancelTracking}
                activityTypes={activityTypes}
                api={api}
            />

            {/* Create Task Modal */}
            <Dialog open={showCreateModal} onOpenChange={(v) => { setShowCreateModal(v); if (!v) setEditingTask(null); }}>
                <DialogContent className="max-w-lg" data-testid="create-task-modal">
                    <DialogHeader>
                        <DialogTitle>{editingTask ? 'Task bearbeiten' : 'Neuen Task erstellen'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                        <div>
                            <Label>Titel *</Label>
                            <Input
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Task-Titel"
                                data-testid="task-title-input"
                            />
                        </div>
                        <div>
                            <Label>Beschreibung</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Beschreibung hinzufügen..."
                                data-testid="task-description-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Status</Label>
                                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                                    <SelectTrigger data-testid="task-status-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {kanbanStatuses.map(([k, v]) => (
                                            <SelectItem key={k} value={k}>{v.emoji} {v.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Priorität</Label>
                                <Select value={String(formData.priority)} onValueChange={(v) => setFormData({ ...formData, priority: Number(v) })}>
                                    <SelectTrigger data-testid="task-priority-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">🔴 Hoch</SelectItem>
                                        <SelectItem value="2">🟡 Mittel</SelectItem>
                                        <SelectItem value="3">⚪ Niedrig</SelectItem>
                                        <SelectItem value="0">🟢 Nicht dringend</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Kategorie (für Workload)</Label>
                                <Select value={formData.category || "none"} onValueChange={(v) => setFormData({ ...formData, category: v === "none" ? "" : v })}>
                                    <SelectTrigger data-testid="task-category-select">
                                        <SelectValue placeholder="Kategorie wählen" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Keine Kategorie</SelectItem>
                                        {settings?.activity_types?.map(cat => (
                                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Geschätzte Stunden</Label>
                                <Input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    placeholder="z.B. 2.5"
                                    value={formData.estimated_hours}
                                    onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                                    data-testid="task-estimated-hours-input"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Faelligkeitsdatum</Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="w-full justify-start font-normal mt-1" data-testid="task-deadline-input">
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {formData.deadline ? format(parseISO(formData.deadline), 'dd. MMM yyyy', { locale: de }) : <span className="text-muted-foreground">Datum waehlen...</span>}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={formData.deadline ? parseISO(formData.deadline) : undefined}
                                            onSelect={(date) => setFormData({ ...formData, deadline: date ? format(date, 'yyyy-MM-dd') : '' })}
                                            locale={de}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div>
                                <Label>Uhrzeit (optional)</Label>
                                <Input
                                    type="time"
                                    value={formData.deadline_time}
                                    onChange={(e) => setFormData({ ...formData, deadline_time: e.target.value })}
                                    data-testid="task-deadline-time-input"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Projekt</Label>
                            <Select value={formData.project_id || "none"} onValueChange={(v) => setFormData({ ...formData, project_id: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="task-project-select">
                                    <SelectValue placeholder="Projekt wählen (optional)" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Kein Projekt</SelectItem>
                                    {projects.map(p => (
                                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Kunde</Label>
                            <Select value={formData.customer_id || "none"} onValueChange={(v) => setFormData({ ...formData, customer_id: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="task-customer-select">
                                    <SelectValue placeholder="Kunde wählen (optional)" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Kein Kunde</SelectItem>
                                    {customers.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Zuweisen an</Label>
                            <Select
                                value={formData.assigned_to[0] || ''}
                                onValueChange={(v) => setFormData({ ...formData, assigned_to: v ? [v] : [] })}
                            >
                                <SelectTrigger data-testid="task-assignee-select">
                                    <SelectValue placeholder="Mitarbeiter wählen" />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.map(u => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                            <span className="text-muted-foreground ml-2 capitalize">({u.display_title || u.role?.replace('_', ' ') || 'Mitarbeiter'})</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Account Manager */}
                        <div>
                            <Label className="flex items-center gap-2">
                                <UserCog className="w-4 h-4 text-primary" />
                                Account Manager
                            </Label>
                            <Select
                                value={formData.account_manager_id || 'none'}
                                onValueChange={(v) => setFormData({ ...formData, account_manager_id: v === "none" ? "" : v })}
                            >
                                <SelectTrigger data-testid="task-account-manager-select">
                                    <SelectValue placeholder="Account Manager wählen (optional)" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Kein Account Manager</SelectItem>
                                    {users.filter(u => u.role === 'account_manager' || u.role === 'chef').map(u => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">
                                Erhält Benachrichtigungen bei Status-, Datei- und Kommentaränderungen
                            </p>
                        </div>
                        
                        {/* Recurring Task Section */}
                        <div className="pt-4 border-t border-border/50">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <RefreshCw className="w-4 h-4 text-primary" />
                                    <Label className="mb-0">Wiederkehrende Aufgabe</Label>
                                </div>
                                <Switch
                                    checked={formData.is_recurring}
                                    onCheckedChange={(v) => setFormData({ ...formData, is_recurring: v, recurrence_pattern: v ? 'weekly' : 'none' })}
                                    data-testid="task-recurring-switch"
                                />
                            </div>
                            {formData.is_recurring && (
                                <Select 
                                    value={formData.recurrence_pattern} 
                                    onValueChange={(v) => setFormData({ ...formData, recurrence_pattern: v })}
                                >
                                    <SelectTrigger data-testid="task-recurrence-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {recurrenceOptions.filter(o => o.value !== 'none').map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>

                        {/* Erinnerungen vor der Deadline (wie beim Kalender) */}
                        <div className="pt-4 border-t border-border/50">
                            <div className="flex items-center gap-2 mb-2">
                                <Bell className="w-4 h-4 text-primary" />
                                <Label className="mb-0">Erinnerung vor der Deadline</Label>
                            </div>
                            <TaskReminderPicker
                                value={formData.reminders || []}
                                onChange={(v) => setFormData({ ...formData, reminders: v })}
                            />
                            {!formData.deadline && (formData.reminders || []).length > 0 && (
                                <p className="text-[11px] text-amber-500 mt-1.5">Ohne Deadline wird keine Erinnerung gesendet.</p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowCreateModal(false); setEditingTask(null); }}>Abbrechen</Button>
                        <Button onClick={editingTask ? handleEditSave : handleCreateTask} disabled={!formData.title || taskSubmitting} data-testid="submit-task-button">
                            {taskSubmitting ? 'Wird gespeichert...' : editingTask ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Task Detail Modal */}
            <AnimatePresence>
                {selectedTask && (
                    <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0 [&>button]:hidden" data-testid="task-detail-modal">
                            {/* Header with action buttons */}
                            <div className="flex items-start justify-between p-4 pb-0">
                                <div className="flex-1 pr-4">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                        <Badge className={statusConfig[selectedTask.status]?.color}>
                                            {statusConfig[selectedTask.status]?.emoji} {statusConfig[selectedTask.status]?.label}
                                        </Badge>
                                        <Badge variant="outline">
                                            {priorityConfig[selectedTask.priority]?.label}
                                        </Badge>
                                        {selectedTask.is_recurring && (
                                            <Badge variant="outline" className="gap-1">
                                                <RefreshCw className="w-3 h-3" />
                                                Wiederkehrend
                                            </Badge>
                                        )}
                                        {selectedTask.is_archived && (
                                            <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-600">
                                                Archiviert
                                            </Badge>
                                        )}
                                    </div>
                                    <h2 className="text-lg font-semibold leading-tight">{selectedTask.title}</h2>
                                </div>
                                
                                {/* Action buttons - aligned top right corner */}
                                <div className="flex items-center gap-1 shrink-0">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="task-actions-menu">
                                                <MoreHorizontal className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => {
                                                setEditingTask(selectedTask);
                                                setFormData({
                                                    title: selectedTask.title || '',
                                                    description: selectedTask.description || '',
                                                    status: selectedTask.status || 'offen',
                                                    priority: selectedTask.priority || 2,
                                                    deadline: selectedTask.deadline || '',
                                                    deadline_time: selectedTask.deadline_time || '',
                                                    assigned_to: selectedTask.assigned_to || [],
                                                    project_id: selectedTask.project_id || '',
                                                    customer_id: selectedTask.customer_id || '',
                                                    account_manager_id: selectedTask.account_manager_id || '',
                                                    category: selectedTask.category || '',
                                                    estimated_hours: selectedTask.estimated_hours || '',
                                                    is_recurring: selectedTask.is_recurring || false,
                                                    recurrence_pattern: selectedTask.recurrence_pattern || 'none',
                                                    reminders: selectedTask.reminders || []
                                                });
                                                setSelectedTask(null);
                                                setShowCreateModal(true);
                                            }} data-testid="edit-task-btn">
                                                <Edit3 className="w-4 h-4 mr-2" />
                                                Bearbeiten
                                            </DropdownMenuItem>
                                            {selectedTask.is_archived ? (
                                                <DropdownMenuItem 
                                                    onClick={() => handleUnarchiveTask(selectedTask.id)}
                                                    data-testid="unarchive-task-btn"
                                                >
                                                    <RotateCcw className="w-4 h-4 mr-2" />
                                                    Wiederherstellen
                                                </DropdownMenuItem>
                                            ) : (
                                                <DropdownMenuItem 
                                                    onClick={() => handleArchiveTask(selectedTask.id)}
                                                    data-testid="archive-task-btn"
                                                >
                                                    <Archive className="w-4 h-4 mr-2" />
                                                    Archivieren
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuItem 
                                                onClick={() => handleDeleteTask(selectedTask.id)}
                                                className="text-destructive"
                                                data-testid="delete-task-btn"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Löschen
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-8 w-8"
                                        onClick={() => setSelectedTask(null)}
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>

                            <ScrollArea className="max-h-[60vh] px-4 pb-4">
                                <div className="space-y-4 pt-3">
                                    {/* Live Time Tracking */}
                                    <LiveTaskTimer
                                        task={selectedTask}
                                        isActive={activeTaskId === selectedTask.id && isTracking}
                                        elapsedSeconds={elapsedSeconds}
                                        onStart={() => handleTimeToggle(selectedTask)}
                                        onStop={() => setShowStopModal(true)}
                                    />

                                    {/* Description */}
                                    {selectedTask.description && (
                                        <div>
                                            <h4 className="text-sm font-medium mb-2">Beschreibung</h4>
                                            <p className="text-muted-foreground">{selectedTask.description}</p>
                                        </div>
                                    )}

                                    {/* Details Grid */}
                                    <div className="grid grid-cols-2 gap-4">
                                        {/* Deadline with time */}
                                        {selectedTask.deadline && (
                                            <div>
                                                <h4 className="text-sm font-medium mb-1">Fälligkeitsdatum</h4>
                                                <p className="text-muted-foreground flex items-center gap-2">
                                                    <CalendarIcon className="w-4 h-4" />
                                                    {selectedTask.deadline.includes('T') 
                                                        ? format(new Date(selectedTask.deadline), 'PPp', { locale: de })
                                                        : format(new Date(selectedTask.deadline), 'PPP', { locale: de })
                                                    }
                                                </p>
                                            </div>
                                        )}
                                        
                                        {/* Assigned users with role */}
                                        {selectedTask.assigned_users?.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-medium mb-1">Zugewiesen</h4>
                                                <div className="space-y-2">
                                                    {selectedTask.assigned_users.map(u => (
                                                        <div key={u.id} className="flex items-center gap-2">
                                                            <Avatar className="w-6 h-6">
                                                                <AvatarFallback style={{ backgroundColor: u.profile_color }} className="text-white text-xs">
                                                                    {u.first_name?.[0]}{u.last_name?.[0]}
                                                                </AvatarFallback>
                                                            </Avatar>
                                                            <div>
                                                                <span className="text-sm">{u.first_name} {u.last_name}</span>
                                                                <span className="text-xs text-muted-foreground ml-2 capitalize">
                                                                    ({u.display_title || u.role?.replace('_', ' ') || 'Mitarbeiter'})
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Account Manager */}
                                    {selectedTask.account_manager && (
                                        <div className="p-3 rounded-xl bg-primary/5 border border-primary/20">
                                            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                                <UserCog className="w-4 h-4 text-primary" />
                                                Account Manager
                                            </h4>
                                            <UserHoverCard user={selectedTask.account_manager}>
                                                <div className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-1 -m-1 rounded-lg transition-colors">
                                                    <Avatar className="w-8 h-8">
                                                        {selectedTask.account_manager.profile_image ? (
                                                            <AvatarImage src={selectedTask.account_manager.profile_image} />
                                                        ) : (
                                                            <AvatarFallback style={{ backgroundColor: selectedTask.account_manager.profile_color }} className="text-white text-sm">
                                                                {selectedTask.account_manager.first_name?.[0]}{selectedTask.account_manager.last_name?.[0]}
                                                            </AvatarFallback>
                                                        )}
                                                    </Avatar>
                                                    <div>
                                                        <p className="text-sm font-medium">
                                                            {selectedTask.account_manager.first_name} {selectedTask.account_manager.last_name}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            Erhält Benachrichtigungen bei Änderungen
                                                        </p>
                                                    </div>
                                                </div>
                                            </UserHoverCard>
                                        </div>
                                    )}

                                    {/* Time per User */}
                                    {timePerUser.length > 0 && (
                                        <div className="p-3 rounded-xl bg-muted/30 border border-border/30">
                                            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                                                <Clock className="w-4 h-4" />
                                                Zeit pro Mitarbeiter
                                            </h4>
                                            <div className="space-y-2">
                                                {timePerUser.map(entry => {
                                                    const hours = Math.floor(entry.total_seconds / 3600);
                                                    const minutes = Math.floor((entry.total_seconds % 3600) / 60);
                                                    return (
                                                        <div key={entry.user_id} className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <Avatar className="w-6 h-6">
                                                                    <AvatarFallback style={{ backgroundColor: entry.profile_color }} className="text-white text-xs">
                                                                        {entry.user_name.split(' ').map(n => n[0]).join('')}
                                                                    </AvatarFallback>
                                                                </Avatar>
                                                                <span className="text-sm">{entry.user_name}</span>
                                                            </div>
                                                            <span className="text-sm font-mono font-medium">
                                                                {hours > 0 ? `${hours}h ` : ''}{minutes}m
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Status Actions */}
                                    <div>
                                        <h4 className="text-sm font-medium mb-2">Status ändern</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {kanbanStatuses.map(([status, config]) => (
                                                <Button
                                                    key={status}
                                                    variant={selectedTask.status === status ? 'default' : 'outline'}
                                                    size="sm"
                                                    onClick={() => handleUpdateTask(selectedTask.id, { status })}
                                                    data-testid={`status-btn-${status}`}
                                                >
                                                    {config.emoji} {config.label}
                                                </Button>
                                            ))}
                                        </div>
                                        {statusConfig[selectedTask.status]?.is_done && (
                                            <p className="text-xs text-muted-foreground mt-2">
                                                💡 Klicke auf einen anderen Status, um den Task wieder zu öffnen
                                            </p>
                                        )}
                                    </div>

                                    {/* Files Section with Drag & Drop */}
                                    <div>
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="text-sm font-medium flex items-center gap-2">
                                                <Paperclip className="w-4 h-4" />
                                                Dateien ({selectedTask.files?.length || 0})
                                            </h4>
                                        </div>
                                        
                                        {/* Drag & Drop Zone */}
                                        <div
                                            ref={dropZoneRef}
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                            onClick={() => !uploading && fileInputRef.current?.click()}
                                            className={`
                                                relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer
                                                transition-all duration-200 mb-3
                                                ${isDragOver 
                                                    ? 'border-primary bg-primary/5' 
                                                    : 'border-border/50 hover:border-primary/50 hover:bg-muted/30'}
                                            `}
                                            data-testid="file-drop-zone"
                                        >
                                            {uploading ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                                    <span className="text-sm text-muted-foreground">Hochladen...</span>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center gap-2">
                                                    <Upload className={`w-8 h-8 ${isDragOver ? 'text-primary' : 'text-muted-foreground'}`} />
                                                    <div>
                                                        <p className="text-sm font-medium">
                                                            {isDragOver ? 'Datei hier ablegen' : 'Dateien hierher ziehen'}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            oder klicken zum Auswählen (max. 5MB)
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            className="hidden"
                                            onChange={handleFileUpload}
                                            accept="*/*"
                                        />
                                        
                                        {selectedTask.files?.length > 0 && (
                                            <div className="space-y-2">
                                                {selectedTask.files.map((file, i) => {
                                                    const FileIcon = getFileIcon(file.filename);
                                                    return (
                                                        <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                                                            <FileIcon className="w-5 h-5 text-muted-foreground" />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-medium truncate">{file.filename}</p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {(file.size / 1024).toFixed(1)} KB
                                                                </p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    {/* Comments */}
                                    <div>
                                        <h4 className="text-sm font-medium mb-3">Kommentare ({selectedTask.comments?.length || 0})</h4>
                                        <div className="space-y-3 mb-4">
                                            {selectedTask.comments?.map(comment => (
                                                <div key={comment.id} className="p-3 rounded-xl bg-muted/50">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-medium text-sm">{comment.user_name}</span>
                                                        <span className="text-xs text-muted-foreground">
                                                            {format(new Date(comment.created_at), 'PPp', { locale: de })}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm">{comment.content}</p>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex gap-2">
                                            <Input
                                                value={newComment}
                                                onChange={(e) => setNewComment(e.target.value)}
                                                placeholder="Kommentar hinzufügen..."
                                                onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                                                data-testid="comment-input"
                                            />
                                            <Button onClick={handleAddComment} data-testid="add-comment-button">
                                                Senden
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </ScrollArea>
                        </DialogContent>
                    </Dialog>
                )}
            </AnimatePresence>

            {/* Stop Time Modal - iOS Style */}
            <Dialog open={showStopModal} onOpenChange={(open) => {
                setShowStopModal(open);
                if (open) {
                    // Pre-select customer from the active timer (e.g. when started from a task tile)
                    if (activeCustomerId && !selectedCustomerForTime) {
                        const c = customers.find(c => c.id === activeCustomerId);
                        if (c) setSelectedCustomerForTime(c);
                    }
                } else {
                    setSelectedCustomerForTime(null);
                    setStopTimeForm({ customer_id: '', task_id: '', activity_type: '', description: '', is_pause: false });
                }
            }}>
                <DialogContent data-testid="stop-time-modal" className="max-w-md p-0 overflow-hidden">
                    {/* Header with gradient */}
                    <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 pb-4">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-3 text-xl">
                                <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center">
                                    <span className="text-2xl">⏱️</span>
                                </div>
                                <div>
                                    <p className="font-semibold">Zeit stoppen</p>
                                    <p className="text-3xl font-mono font-bold text-primary">{formattedTime}</p>
                                </div>
                            </DialogTitle>
                        </DialogHeader>
                    </div>
                    
                    <div className="p-6 pt-2 space-y-5">
                        {/* Quick Actions - iOS Style Cards */}
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => {
                                    handleStopTime(null, 'Pause');
                                    setShowStopModal(false);
                                }}
                                className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 transition-all hover:scale-[1.02]"
                            >
                                <span className="text-3xl">☕</span>
                                <span className="font-medium text-amber-700 dark:text-amber-400">Pause</span>
                            </button>
                            <button
                                onClick={() => {
                                    handleStopTime(null, 'Allgemein');
                                    setShowStopModal(false);
                                }}
                                className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-slate-500/10 hover:bg-slate-500/20 border border-slate-500/20 transition-all hover:scale-[1.02]"
                            >
                                <span className="text-3xl">📋</span>
                                <span className="font-medium text-slate-700 dark:text-slate-400">Allgemein</span>
                            </button>
                        </div>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-dashed" />
                            </div>
                            <div className="relative flex justify-center">
                                <span className="bg-background px-3 text-xs text-muted-foreground uppercase tracking-wider">oder buchen auf</span>
                            </div>
                        </div>

                        {/* Customer Selection with Logos */}
                        <div>
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <span>🏢</span> Kunde
                            </Label>
                            <div className="mt-2 max-h-36 overflow-y-auto space-y-1.5 pr-1">
                                {customers.map(customer => (
                                    <button
                                        key={customer.id}
                                        onClick={() => setSelectedCustomerForTime(customer)}
                                        className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                                            selectedCustomerForTime?.id === customer.id 
                                                ? 'border-primary bg-primary/10' 
                                                : 'border-border/50 hover:border-primary/50 hover:bg-muted/30'
                                        }`}
                                    >
                                        <div 
                                            className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0 overflow-hidden"
                                            style={{ backgroundColor: customer.ci_primary_color || customer.ci_colors?.[0] || '#009dde' }}
                                        >
                                            {customer.logos?.[0] ? (
                                                <img 
                                                    src={typeof customer.logos[0] === 'string' 
                                                        ? `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${customer.logos[0].split('/').pop()}`
                                                        : customer.logos[0]?.url || ''
                                                    } 
                                                    alt="" 
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                customer.name?.[0]?.toUpperCase()
                                            )}
                                        </div>
                                        <span className="font-medium truncate">{customer.name}</span>
                                        {selectedCustomerForTime?.id === customer.id && (
                                            <span className="ml-auto text-primary">✓</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Activity Type Dropdown */}
                        <div>
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <span>🎯</span> Aktivität
                            </Label>
                            <Select 
                                value={stopTimeForm.activity_type} 
                                onValueChange={(val) => setStopTimeForm({ ...stopTimeForm, activity_type: val })}
                            >
                                <SelectTrigger className="mt-2 h-12 rounded-xl">
                                    <SelectValue placeholder="Was hast du gemacht?" />
                                </SelectTrigger>
                                <SelectContent>
                                    {activityTypes.map(type => (
                                        <SelectItem key={type} value={type}>
                                            <div className="flex items-center gap-2">
                                                <span className="text-lg">
                                                    {type === 'Grafik' && '🎨'}
                                                    {type === 'Videoschnitt' && '🎬'}
                                                    {type === 'Reporting' && '📊'}
                                                    {type === 'Storyboard' && '📝'}
                                                    {type === 'Entwicklung' && '💻'}
                                                    {type === 'Meeting' && '👥'}
                                                    {type === 'Planung' && '📅'}
                                                    {type === 'Support' && '🛟'}
                                                    {!['Grafik', 'Videoschnitt', 'Reporting', 'Storyboard', 'Entwicklung', 'Meeting', 'Planung', 'Support'].includes(type) && '📌'}
                                                </span>
                                                {type}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Note */}
                        <div>
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <span>💬</span> Notiz <span className="text-muted-foreground font-normal">(optional)</span>
                            </Label>
                            <Textarea
                                value={stopTimeForm.description}
                                onChange={(e) => setStopTimeForm({ ...stopTimeForm, description: e.target.value })}
                                placeholder="Kurze Beschreibung deiner Arbeit..."
                                className="mt-2 rounded-xl resize-none"
                                rows={2}
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex gap-3 p-6 pt-0">
                        <Button 
                            variant="ghost" 
                            onClick={() => { setShowStopModal(false); setSelectedCustomerForTime(null); }} 
                            className="flex-1 h-12 rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                            <X className="w-4 h-4 mr-2" />
                            Verwerfen
                        </Button>
                        <Button 
                            onClick={() => {
                                let desc = stopTimeForm.description || '';
                                if (stopTimeForm.activity_type) {
                                    desc = `[${stopTimeForm.activity_type}] ${desc}`;
                                }
                                handleStopTime(selectedCustomerForTime?.id || null, selectedCustomerForTime?.name || 'Allgemein', desc);
                                setSelectedCustomerForTime(null);
                                setStopTimeForm({ customer_id: '', task_id: '', activity_type: '', description: '', is_pause: false });
                            }}
                            data-testid="save-time-button"
                            className="flex-1 h-12 rounded-xl bg-primary hover:bg-primary/90"
                        >
                            ✨ Zeit speichern
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Switch Task Modal */}
            <Dialog open={showSwitchModal} onOpenChange={setShowSwitchModal}>
                <DialogContent data-testid="switch-task-modal">
                    <DialogHeader>
                        <DialogTitle>Zeit wechseln?</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-muted-foreground">
                            Du trackst gerade Zeit. Möchtest du die aktuelle Zeit beenden und zu 
                            "<span className="font-medium text-foreground">{pendingTaskSwitch?.title}</span>" wechseln?
                        </p>
                        <p className="text-sm text-muted-foreground">
                            Aktuelle Zeit: <span className="font-mono font-semibold">{formattedTime}</span>
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowSwitchModal(false); setPendingTaskSwitch(null); }}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleSwitchTask} data-testid="confirm-switch-button">
                            Zeit wechseln
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TasksPage;
